import React, { useState } from 'react';
import { Mail, Lock, ArrowRight, Eye, EyeOff, BarChart3, Wrench, LineChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { brandLogos, brandName } from '../config/brand';
import { partnerSignupUrl } from '../config/partnerPanel';
import { PartnerPanelLink } from './PartnerPanelLink';

export const PartnerLogin: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string>('');

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setApiError('');
    
    try {
      console.log('🔍 Starting login request...');
      const response = await apiService.loginPartner({
        email: formData.email,
        password: formData.password
      });

      console.log('📥 Login response received:', response);
      console.log('📊 Response status:', response.status);
      console.log('📊 Response data:', response.data);

      if (response.status?.success) {
        // Login successful
        console.log('✅ Login successful:', response.data);
        // Get token from response
        const token = response.data?.token;
        
        console.log('🔑 Token extracted:', token ? 'Token found' : 'Token NOT found');
        console.log('🔑 Full data object:', JSON.stringify(response.data, null, 2));
        
        if (token) {
          // Get partner panel URL from environment variable or default
          // Format: http://localhost:3000 (development) or https://partner.trendingobjects.com (production)
          const partnerPanelUrl = process.env.REACT_APP_PARTNER_PANEL_URL || 'https://partner.trendingobjects.com';
          
          console.log('🌐 Redirecting to partner panel:', partnerPanelUrl);
          
          // Redirect to partner panel with token in URL query parameter
          window.location.href = `${partnerPanelUrl}/login?token=${encodeURIComponent(token)}`;
        } else {
          console.error('❌ No token in response.data:', response.data);
          setApiError('Login successful but no token received. Response: ' + JSON.stringify(response.data));
        }
      } else {
        // Login failed
        console.error('❌ Login failed:', response.status);
        setApiError(response.status?.message || 'Login failed. Please check your credentials and try again.');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('❌ Error details:', error instanceof Error ? error.message : error);
      setApiError('Login failed. Please check your credentials and try again. Error: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="io-site partner-auth-page partner-login">
      <div className="login-layout">
        {/* Left Side - Banner Content */}
        <div className="banner-content">
          <div className="banner-text">
            <div className="logo-container">
              <img
                src={brandLogos.markDark}
                alt={`${brandName} logo`}
                className="partner-logo"
              />
              <div className="logo-divider"></div>
            </div>
            <h1>Welcome Back, Partner</h1>
            <p>
              Access your Partner Panel and continue building amazing IoT solutions with{' '}
              {brandName}&apos;s comprehensive platform.
            </p>
            <div className="banner-features">
              <div className="feature-item">
                <div className="feature-icon feature-icon--lucide" aria-hidden>
                  <BarChart3 size={22} strokeWidth={2} />
                </div>
                <span>Dashboard Analytics</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon feature-icon--lucide" aria-hidden>
                  <Wrench size={22} strokeWidth={2} />
                </div>
                <span>Device Management</span>
              </div>
              <div className="feature-item">
                <div className="feature-icon feature-icon--lucide" aria-hidden>
                  <LineChart size={22} strokeWidth={2} />
                </div>
                <span>Performance Insights</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-container">
          <div className="login-header">
            <h2>Partner Login</h2>
            <p>Sign in to your partner account</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {/* API Error Display */}
            {apiError && (
              <div className={`api-error ${apiError.includes('already exists') ? 'warning' : ''}`}>
                <span>{apiError}</span>
              </div>
            )}

            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                <Mail size={18} />
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder="Enter your partner email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            {/* Password Field */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                <Lock size={18} />
                Password *
              </label>
              <div className="password-input-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="login-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">Remember me</span>
              </label>
              <Link to="/forgot-password" className="forgot-password">
                Forgot Password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="submit-button"
            >
              {isLoading ? (
                'Signing In...'
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>Don't have a partner account? <PartnerPanelLink to={partnerSignupUrl()}>Register here</PartnerPanelLink></p>
          </div>
        </div>
      </div>
    </div>
  );
};
